/**
 * Thin client for the `invoice-generation-api` edge function (issue #287).
 * Every action requires `companyId` (the caller's active company — RLS
 * double-enforces server-side via `can_modify_company`, so a spoofed id is
 * rejected, not trusted).
 */
import { supabase } from "@/integrations/supabase/client";
import { extractFunctionError } from "@/utils/errorUtils";

export interface InvoiceGenerationApiError extends Error {
  errorType?: string;
  // Read by userFacingErrorMessage() to withhold a server fault's text (FIN-1272).
  status?: number;
  reference?: string;
  details?: { missing?: string[]; [key: string]: unknown };
}

async function invoke<T>(action: string, companyId: string, params: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await supabase.functions.invoke("invoice-generation-api", {
    body: { action, companyId, ...params },
  });

  if (error) {
    const functionError = await extractFunctionError(error);
    const message = functionError?.message ?? error.message ?? `invoice-generation-api action '${action}' failed`;
    const wrapped: InvoiceGenerationApiError = new Error(message);
    wrapped.errorType = functionError?.error;
    wrapped.details = functionError?.details;
    wrapped.status = (error as { context?: { status?: number } }).context?.status;
    wrapped.reference = typeof functionError?.reference === "string" ? functionError.reference : undefined;
    throw wrapped;
  }

  return data as T;
}

export const invoiceGenerationApiClient = {
  getSellerProfile: (companyId: string) => invoke<{ seller: unknown }>("getSellerProfile", companyId),
  updateSellerProfile: (companyId: string, input: unknown) => invoke<{ seller: unknown }>("updateSellerProfile", companyId, { input }),
  updateLogo: (companyId: string, logoPath: string) =>
    invoke<{ logoUrl: string | null }>("updateLogo", companyId, { logoPath }),
  listCustomers: (companyId: string) => invoke<{ customers: unknown[] }>("listCustomers", companyId),
  createCustomer: (companyId: string, input: unknown) => invoke<{ customer: unknown }>("createCustomer", companyId, { input }),
  suggestArticles: (companyId: string) => invoke<{ articles: unknown[] }>("suggestArticles", companyId),
  classifyLineVat: (companyId: string, description: string) =>
    invoke<{ category: string | null; rate: number }>("classifyLineVat", companyId, { description }),
  /** Create-only. An edit of a saved draft goes through updateReceivableDraft. */
  draftReceivable: (companyId: string, input: unknown) =>
    invoke<{ receivable: unknown; lineItems: unknown[]; vatByLine: unknown[]; totals: unknown; pdfPath: string }>(
      "draftReceivable", companyId, { input },
    ),
  /**
   * Saves an edit to a saved draft at `expectedVersion` (FIN-829). Refusals arrive with
   * errorType DRAFT_NO_LONGER_EDITABLE (sent), DRAFT_VERSION_CONFLICT (saved by someone
   * else) or NOT_FOUND (deleted).
   */
  updateReceivableDraft: (companyId: string, receivableId: string, expectedVersion: number, input: unknown) =>
    invoke<{ receivable: unknown; lineItems: unknown[]; vatByLine: unknown[]; totals: unknown; pdfPath: string }>(
      "updateReceivableDraft", companyId, { receivableId, expectedVersion, input },
    ),
  /** A draft's viewable PDF path, rendered first if it has none yet (FIN-829 EC8). */
  renderReceivableDraftPdf: (companyId: string, receivableId: string) =>
    invoke<{ pdfPath: string }>("renderReceivableDraftPdf", companyId, { receivableId }),
  validateReceivable: (companyId: string, receivableId: string) =>
    invoke<{ ok: boolean; missing: string[] }>("validateReceivable", companyId, { receivableId }),
  /** Read-only — safe for an already-sent receivable. `draftPdfPath` is what View shows for a draft. */
  getReceivable: (companyId: string, receivableId: string) =>
    invoke<{ receivable: unknown; lineItems: unknown[]; draftPdfPath: string | null }>("getReceivable", companyId, { receivableId }),
  /**
   * Mark-as-sent. The action name is still "issueReceivable" — 'issued' is the stored
   * issue_status value and the RPC's own name; the user-facing action is "Mark as sent"
   * (FIN-603 F4).
   */
  issueReceivable: (companyId: string, receivableId: string) =>
    invoke<{ invoiceNumber: string; ocr: string; pdfPath: string; toPay: number; receivable: unknown; alreadyIssued: boolean }>(
      "issueReceivable", companyId, { receivableId },
    ),
  deleteReceivableDraft: (companyId: string, receivableId: string) =>
    invoke<{ deleted: boolean }>("deleteReceivableDraft", companyId, { receivableId }),
  duplicateReceivable: (companyId: string, sourceReceivableId: string) =>
    invoke<{ receivable: unknown; lineItems: unknown[]; vatByLine: unknown[]; totals: unknown; pdfPath: string }>(
      "duplicateReceivable", companyId, { sourceReceivableId },
    ),
  listReceivables: (companyId: string) => invoke<{ receivables: unknown[] }>("listReceivables", companyId),
  listUploadedReceivables: (companyId: string) =>
    invoke<{ uploadedReceivables: unknown[] }>("listUploadedReceivables", companyId),
  listManualBankAccounts: (companyId: string) =>
    invoke<{ manualBankAccounts: unknown[] }>("listManualBankAccounts", companyId),
  createManualBankAccount: (companyId: string, input: unknown) =>
    invoke<{ manualBankAccount: unknown }>("createManualBankAccount", companyId, { input }),
  setBankAccountBankgiro: (companyId: string, bankAccountId: string, bankgiro: string) =>
    invoke<{ seller: unknown }>("setBankAccountBankgiro", companyId, { bankAccountId, bankgiro }),
  setCompanyBankgiro: (companyId: string, bankgiro: string) =>
    invoke<{ seller: unknown }>("setCompanyBankgiro", companyId, { bankgiro }),
  removeCompanyBankgiro: (companyId: string) =>
    invoke<{ seller: unknown }>("removeCompanyBankgiro", companyId),
  removeBankAccountBankgiro: (companyId: string, bankAccountId: string) =>
    invoke<{ seller: unknown }>("removeBankAccountBankgiro", companyId, { bankAccountId }),
  dismissBankgiroReplacementNotice: (companyId: string, bankAccountId: string) =>
    invoke<{ seller: unknown }>("dismissBankgiroReplacementNotice", companyId, { bankAccountId }),
};
